import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import gql from 'graphql-tag';
import React, { Fragment, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Mutation, Query } from 'react-apollo';
import { CSVLink } from 'react-csv';
import GetAppIcon from '@material-ui/icons/GetApp';
import { ADMIN_BULK_CREATE_PRODUCTS } from '../../libs/Mutations';

const MANUFACTURERS_QUERY = gql`
  query adminManufacturersForProductImport($filter: String) {
    admin {
      manufacturers(filter: $filter) {
        manufacturerId
        displayName
      }
    }
  }
`;

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(3),
        margin: theme.spacing(2),
        background: theme.palette.common.white,
    },
    table: { marginTop: theme.spacing(2), maxHeight: 400, overflow: 'auto' },
}));

const FIELD_KEYS = [
    { key: 'model', label: 'Model name' },
    { key: 'modelNameType', label: 'Type (CEC or Internal)' },
    { key: 'brand', label: 'Brand' },
    { key: 'productType', label: 'Product type' },
    {
        key: 'batteryComponentType',
        label: 'Internal component type (MODULAR, MASTER, …)',
    },
    {
        key: 'cecEquipmentCategory',
        label: 'CEC equipment category (optional, from CEC listing)',
    },
    { key: 'isSmart', label: 'Is smart (true/false)' },
    { key: 'nominalStorageKwh', label: 'Nominal storage (kWh)' },
    { key: 'nominalUsableKwh', label: 'Nominal usable (kWh)' },
    { key: 'nominalVoltage', label: 'Nominal voltage' },
    { key: 'nominalMaxPowerKw', label: 'Nominal max power (kW)' },
    { key: 'wattage', label: 'Wattage' },
    { key: 'firetested', label: 'Fire tested' },
    { key: 'approvedDate', label: 'Approved date' },
    { key: 'expirydate', label: 'Expiry date' },
];

const SAMPLE_CSV_HEADERS = FIELD_KEYS.map(({ key, label }) => ({ label, key }));

const SAMPLE_CSV_DATA = [
    {
        model: 'Example Model ABC-100',
        modelNameType: 'CEC',
        brand: 'Example Brand',
        productType: 'BATTERY',
        batteryComponentType: 'MODULAR',
        cecEquipmentCategory: '',
        isSmart: 'true',
        nominalStorageKwh: '13.5',
        nominalUsableKwh: '12.5',
        nominalVoltage: '48',
        nominalMaxPowerKw: '5',
        wattage: '350',
        firetested: 'Yes',
        approvedDate: '2025-01-15',
        expirydate: '2030-01-15',
    },
];

function parseCsvLine(line) {
    const result = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i += 1) {
        const c = line[i];
        if (c === '"') {
            inQ = !inQ;
        } else if (!inQ && c === ',') {
            result.push(cur.trim());
            cur = '';
        } else {
            cur += c;
        }
    }
    result.push(cur.trim());
    return result.map((cell) => cell.replace(/^"|"$/g, ''));
}

function parseCsv(text) {
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
    if (!lines.length) return { headers: [], rows: [] };
    const headers = parseCsvLine(lines[0]);
    const rows = lines.slice(1).map(parseCsvLine);
    return { headers, rows };
}

function norm(s) {
    return String(s || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}

function guessMapping(headers) {
    const map = {};
    const synonyms = {
        model: ['model', 'cecmodel', 'displaymodel', 'modelname'],
        modelNameType: ['modelnametype', 'nametype', 'modelsource'],
        brand: ['brand', 'manufacturer'],
        productType: ['producttype', 'type'],
        batteryComponentType: [
            'batterycomponenttype',
            'internalcomponenttype',
            'componenttypeinternal',
        ],
        cecEquipmentCategory: [
            'cecequipmentcategory',
            'equipmentcategory',
            'ceccategory',
            'ceccomponent',
        ],
        isSmart: ['issmart', 'smart'],
        nominalStorageKwh: ['nominalstoragekwh', 'storagekwh', 'nominalstorage'],
        nominalUsableKwh: ['nominalusablekwh', 'usablekwh'],
        nominalVoltage: ['nominalvoltage', 'voltage'],
        nominalMaxPowerKw: ['nominalmaxpowerkw', 'maxpowerkw', 'maxpower'],
        wattage: ['wattage', 'watts'],
        firetested: ['firetested', 'firetest'],
        approvedDate: ['approveddate', 'approvaldate'],
        expirydate: ['expirydate', 'expiry', 'expdate'],
    };
    headers.forEach((h, idx) => {
        const n = norm(h);
        Object.keys(synonyms).forEach((field) => {
            if (synonyms[field].includes(n)) {
                if (map[field] === undefined) map[field] = idx;
            }
        });
    });
    return map;
}

function cellAt(row, idx) {
    if (idx === undefined || idx === '' || idx === null) return '';
    return row[idx] != null ? String(row[idx]) : '';
}

function normalizeDate(v) {
    if (!v || String(v).trim() === '') return null;
    const s = String(v).trim();

    const sep = s.includes('/') ? '/' : '-';
    const parts = s.split(sep);
    if (parts.length !== 3) return s;

    const [a, b, c] = parts.map((p) => p.trim());

    if (a.length === 4) return `${a}-${b.padStart(2, '0')}-${c.padStart(2, '0')}`;

    if (c.length === 4) {
        const n1 = parseInt(a, 10);
        const n2 = parseInt(b, 10);
        if (n1 > 12) return `${c}-${b.padStart(2, '0')}-${a.padStart(2, '0')}`;
        if (n2 > 12) return `${c}-${a.padStart(2, '0')}-${b.padStart(2, '0')}`;
        return `${c}-${b.padStart(2, '0')}-${a.padStart(2, '0')}`;
    }

    return s;
}

function rowToProduct(row, mapping) {
    const g = (k) => cellAt(row, mapping[k]);
    const parseBool = (v) => {
        const t = String(v || '').toLowerCase();
        return t === 'true' || t === '1' || t === 'yes' || t === 'y';
    };
    const parseNum = (v) => {
        if (v === '' || v == null) return null;
        const n = parseFloat(String(v).replace(/,/g, ''));
        return Number.isFinite(n) ? n : null;
    };
    const parseInt10 = (v) => {
        if (v === '' || v == null) return null;
        const n = parseInt(String(v).replace(/,/g, ''), 10);
        return Number.isFinite(n) ? n : null;
    };

    const rawSmart = g('isSmart');
    const product = {
        model: g('model') || null,
        modelNameType: g('modelNameType') || 'CEC',
        brand: g('brand') || null,
        productType: g('productType') || null,
        batteryComponentType: g('batteryComponentType') || null,
        cecEquipmentCategory: g('cecEquipmentCategory') || null,
        ...(rawSmart !== '' && rawSmart != null ? { isSmart: parseBool(rawSmart) } : {}),
        nominalStorageKwh: parseNum(g('nominalStorageKwh')),
        nominalUsableKwh: parseNum(g('nominalUsableKwh')),
        nominalVoltage: g('nominalVoltage') || null,
        nominalMaxPowerKw: parseNum(g('nominalMaxPowerKw')),
        wattage: parseInt10(g('wattage')),
        firetested: g('firetested') || null,
        approvedDate: normalizeDate(g('approvedDate')),
        expirydate: normalizeDate(g('expirydate')),
    };

    Object.keys(product).forEach((k) => {
        if (product[k] === '' || product[k] === undefined) {
            delete product[k];
        }
    });

    return product;
}

const AdminGlobalProductTypes = () => {
    const classes = useStyles();
    const [manufacturerId, setManufacturerId] = useState('');
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);
    const [mapping, setMapping] = useState({});
    const [parseError, setParseError] = useState(null);
    const [fileName, setFileName] = useState(null);

    const onFile = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        if (!/\.csv$/i.test(file.name)) {
            setParseError('Please upload a .csv file (export Excel to CSV if needed).');
            setHeaders([]);
            setRows([]);
            setFileName(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const text = reader.result;
                const parsed = parseCsv(text);
                setHeaders(parsed.headers);
                setRows(parsed.rows);
                setMapping(guessMapping(parsed.headers));
                setParseError(null);
                setFileName(file.name);
            } catch (err) {
                setParseError(err.message || 'Could not parse CSV');
                setHeaders([]);
                setRows([]);
            }
        };
        reader.readAsText(file);
    };

    const setMap = (field) => (e) => {
        const v = e.target.value;
        setMapping((prev) => ({
            ...prev,
            [field]: v === '' ? undefined : parseInt(v, 10),
        }));
    };

    const buildProducts = () => rows.map((row) => rowToProduct(row, mapping));

    const preview = rows.slice(0, 8);
    const colOpts = [
        <MenuItem key="none" value="">
            <em>(not mapped)</em>
        </MenuItem>,
    ].concat(
        headers.map((h, i) => (
            <MenuItem key={i} value={i}>
                {h}
            </MenuItem>
        ))
    );

    return (
        <Query
            query={MANUFACTURERS_QUERY}
            variables={{ filter: '' }}
            fetchPolicy="network-only"
        >
            {({ loading, error, data }) => (
                <Mutation mutation={ADMIN_BULK_CREATE_PRODUCTS}>
                    {(bulkCreate, { loading: importLoading, data: importData, error: importError }) => {
                        const manufacturers =
                            data && data.admin && data.admin.manufacturers
                                ? data.admin.manufacturers
                                : [];

                        const handleImport = () => {
                            bulkCreate({
                                variables: {
                                    manufacturerId: parseInt(manufacturerId, 10),
                                    products: buildProducts(),
                                },
                            });
                        };

                        const results =
                            importData &&
                            importData.adminMutation &&
                            importData.adminMutation.adminBulkCreateProducts
                                ? importData.adminMutation.adminBulkCreateProducts
                                : null;

                        return (
                            <Fragment>
                                <Helmet>
                                    <title>Global product types (CSV) — Admin</title>
                                </Helmet>
                                <Paper className={classes.paper} elevation={0}>
                                    <Typography variant="h6" gutterBottom>
                                        Global product types — CSV import
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" paragraph>
                                        Select a manufacturer, then upload a CSV of product definitions. Rows are created under
                                        that manufacturer&apos;s catalog (same rules as manufacturer portal bulk import). At least
                                        a <strong>model</strong> name is required per row.
                                    </Typography>

                                    <Button
                                        variant="text"
                                        color="primary"
                                        size="small"
                                        startIcon={<GetAppIcon />}
                                        style={{ marginBottom: 16, textTransform: 'none' }}
                                        component={CSVLink}
                                        headers={SAMPLE_CSV_HEADERS}
                                        data={SAMPLE_CSV_DATA}
                                        filename="product_types_sample.csv"
                                    >
                                        Download sample CSV template
                                    </Button>

                                    {loading && <Typography>Loading manufacturers…</Typography>}
                                    {error && (
                                        <Typography color="error" paragraph>
                                            {error.message || 'Failed to load manufacturers'}
                                        </Typography>
                                    )}

                                    {!loading && !error && (
                                        <FormControl variant="outlined" fullWidth style={{ marginBottom: 24 }} required>
                                            <InputLabel id="mfg-label">Manufacturer</InputLabel>
                                            <Select
                                                labelId="mfg-label"
                                                label="Manufacturer"
                                                value={manufacturerId}
                                                onChange={(e) => setManufacturerId(e.target.value)}
                                            >
                                                <MenuItem value="">
                                                    <em>Select manufacturer</em>
                                                </MenuItem>
                                                {manufacturers.map((m) => (
                                                    <MenuItem key={m.manufacturerId} value={String(m.manufacturerId)}>
                                                        {m.displayName} (ID {m.manufacturerId})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    <Button variant="outlined" component="label" disabled={!manufacturerId}>
                                        Choose CSV file
                                        <input type="file" accept=".csv,text/csv" hidden onChange={onFile} />
                                    </Button>
                                    {fileName && (
                                        <Typography variant="body2" style={{ marginTop: 8 }}>
                                            Selected: {fileName} — {rows.length} data row(s)
                                        </Typography>
                                    )}
                                    {parseError && (
                                        <Typography color="error" style={{ marginTop: 8 }}>
                                            {parseError}
                                        </Typography>
                                    )}

                                    {headers.length > 0 && (
                                        <Fragment>
                                            <Typography variant="subtitle1" style={{ marginTop: 24 }}>
                                                Column mapping
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {FIELD_KEYS.map(({ key, label }) => (
                                                    <Grid item xs={12} sm={6} md={4} key={key}>
                                                        <FormControl variant="outlined" fullWidth size="small">
                                                            <InputLabel>{label}</InputLabel>
                                                            <Select
                                                                value={
                                                                    mapping[key] !== undefined && mapping[key] !== null
                                                                        ? String(mapping[key])
                                                                        : ''
                                                                }
                                                                onChange={setMap(key)}
                                                                label={label}
                                                            >
                                                                {colOpts}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                ))}
                                            </Grid>

                                            <Typography variant="subtitle1" style={{ marginTop: 16 }}>
                                                Preview (first rows)
                                            </Typography>
                                            <Table size="small" className={classes.table} stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        {headers.map((h) => (
                                                            <TableCell key={h}>{h}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {preview.map((r, i) => (
                                                        <TableRow key={i}>
                                                            {r.map((c, j) => (
                                                                <TableCell key={j}>{c}</TableCell>
                                                            ))}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>

                                            <Button
                                                style={{ marginTop: 16 }}
                                                variant="contained"
                                                color="primary"
                                                disabled={importLoading || !rows.length || !manufacturerId}
                                                onClick={handleImport}
                                            >
                                                {importLoading ? 'Importing…' : 'Import all rows'}
                                            </Button>
                                            {importError && (
                                                <Typography color="error" style={{ marginTop: 8 }}>
                                                    {importError.graphQLErrors && importError.graphQLErrors.length
                                                        ? importError.graphQLErrors.map((e) => e.message).join(' ')
                                                        : importError.message}
                                                </Typography>
                                            )}
                                            {results && (
                                                <Table size="small" style={{ marginTop: 16 }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>#</TableCell>
                                                            <TableCell>OK</TableCell>
                                                            <TableCell>Model</TableCell>
                                                            <TableCell>Error</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {results.map((r, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>{r.rowIndex}</TableCell>
                                                                <TableCell>{r.success ? 'Yes' : 'No'}</TableCell>
                                                                <TableCell>{r.model}</TableCell>
                                                                <TableCell>{r.error || ''}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            )}
                                        </Fragment>
                                    )}
                                </Paper>
                            </Fragment>
                        );
                    }}
                </Mutation>
            )}
        </Query>
    );
};

export default AdminGlobalProductTypes;
